import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "@/utils/BaseQuery";
import { ApiResponse } from "@/types/api.types";
import {
  IClubRegistrationData,
  IClubRegistrationResponse,
  ClubRegistrationListQueryParams,
  PaginatedClubRegistrationResponse,
  ApproveClubRegistrationPayload,
  AvailableHarborsResponse,
} from "@/types/club-registration.types";

export const clubRegistrationApi = createApi({
  reducerPath: "clubRegistrationApi",
  baseQuery,
  tagTypes: ["ClubRegistrations"],
  endpoints: (builder) => ({
    getClubRegistrations: builder.query<
      PaginatedClubRegistrationResponse,
      ClubRegistrationListQueryParams
    >({
      query: (params) => ({
        url: "club-registration",
        method: "GET",
        params: params || undefined,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((registration) => ({
                type: "ClubRegistrations" as const,
                id: registration.id,
              })),
              { type: "ClubRegistrations", id: "LIST" },
            ]
          : [{ type: "ClubRegistrations", id: "LIST" }],
      keepUnusedDataFor: 0,
    }),

    getClubRegistrationById: builder.query<IClubRegistrationData, string>({
      query: (id) => ({
        url: `club-registration/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "ClubRegistrations", id }],
    }),

    approveClubRegistration: builder.mutation<
      ApiResponse<IClubRegistrationResponse>,
      { id: string; data: ApproveClubRegistrationPayload }
    >({
      query: ({ id, data }) => ({
        url: `club-registration/${id}/approve`,
        method: "POST",
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ClubRegistrations", id },
        { type: "ClubRegistrations", id: "LIST" },
      ],
    }),

    rejectClubRegistration: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `club-registration/${id}/reject`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "ClubRegistrations", id },
        { type: "ClubRegistrations", id: "LIST" },
      ],
    }),

    getAvailableHarbors: builder.query<AvailableHarborsResponse, string>({
      query: (id) => ({
        url: `club-registration/${id}/available-harbors`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "ClubRegistrations", id }],
    }),
  }),
});

export const {
  useGetClubRegistrationsQuery,
  useGetClubRegistrationByIdQuery,
  useApproveClubRegistrationMutation,
  useRejectClubRegistrationMutation,
  useGetAvailableHarborsQuery,
} = clubRegistrationApi;
